import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from './client';

export interface IsolationBlock {
  status: 'shared' | 'isolated';
  dbConnectionRef: string | null;
  storageBucket: string | null;
  kmsKeyArn: string | null;
}

export interface IsolationSettingsInput {
  dbConnectionRef: string | null;
  storageBucket: string | null;
  kmsKeyArn: string | null;
}

export interface ApplyIsolationInput extends IsolationSettingsInput {
  confirmOrgName?: string;
}

export interface IsolationFieldValidation {
  ok: boolean;
  detail: string;
}

export interface IsolationPreview {
  validation: {
    dbConnectionRef: IsolationFieldValidation;
    storageBucket: IsolationFieldValidation;
    kmsKeyArn: IsolationFieldValidation;
  };
  destructive: boolean;
  impact: { orphanedPatientCount: number; orphanedCaseCount: number };
}

export interface OrgDto {
  id: string;
  name: string;
  region: 'uk' | 'us';
  status: string;
  // Total cases for this org. Returned by list / detail endpoints; may be
  // absent on freshly-created orgs (mutation responses don't re-aggregate).
  caseCount?: number;
  /**
   * True when this org has a row in `organisation_derm_config`. Used
   * by the "Copy DERM config from…" picker so operators only see
   * orgs that actually have something to copy.
   */
  hasDermConfig?: boolean;
  /**
   * One-way synthetic-tenant marker. When true, this org is reserved for
   * the platform simulator: bulk-purge / complete-as-system admin actions
   * (and the simulator's pre-flight) refuse to operate on non-synthetic
   * orgs. Surfaced so operators can tell test orgs apart from real ones.
   */
  synthetic?: boolean;
  /**
   * Single-level hierarchy link. `null` = a root org. Non-null = this org is a
   * child (clinic/site) of the referenced parent. Returned by the list + detail
   * endpoints. Managed via the set/clear-parent endpoints (#1241).
   */
  parentOrganisationId: string | null;
  isolation?: IsolationBlock;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateOrgInput {
  name: string;
  region: 'uk' | 'us';
  synthetic?: boolean;
}

export interface UpdateOrgInput {
  name?: string;
}

const orgsKey = (includeArchived: boolean) => ['orgs', { includeArchived }] as const;
const orgKey = (id: string) => ['org', id] as const;

export function useOrgs(opts: { includeArchived?: boolean } = {}) {
  const includeArchived = opts.includeArchived ?? false;
  return useQuery({
    queryKey: orgsKey(includeArchived),
    queryFn: () =>
      apiGet<OrgDto[]>(includeArchived ? '/api/orgs?includeArchived=true' : '/api/orgs'),
  });
}

export function useOrg(id: string | undefined) {
  return useQuery({
    queryKey: orgKey(id ?? ''),
    queryFn: () => apiGet<OrgDto>(`/api/orgs/${id}`),
    enabled: !!id,
  });
}

export function useCreateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOrgInput) => apiPost<OrgDto>('/api/orgs', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgs'] });
    },
  });
}

export function useUpdateOrg(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateOrgInput) => apiPatch<OrgDto>(`/api/orgs/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgs'] });
      qc.invalidateQueries({ queryKey: orgKey(id) });
    },
  });
}

export function useDeleteOrg(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete(`/api/orgs/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgs'] });
      qc.invalidateQueries({ queryKey: orgKey(id) });
    },
  });
}

export function useRestoreOrg(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<OrgDto>(`/api/orgs/${id}/restore`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgs'] });
      qc.invalidateQueries({ queryKey: orgKey(id) });
    },
  });
}

export function useOrgChildren(id: string | undefined) {
  return useQuery({
    queryKey: [...orgKey(id ?? ''), 'children'],
    queryFn: () => apiGet<OrgDto[]>(`/api/orgs/${id}/children`),
    enabled: !!id,
  });
}

export function useSetOrgParent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (parentOrganisationId: string) =>
      apiPut<OrgDto>(`/api/orgs/${id}/parent`, { parentOrganisationId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgs'] });
      qc.invalidateQueries({ queryKey: orgKey(id) });
    },
  });
}

export function useClearOrgParent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete<OrgDto>(`/api/orgs/${id}/parent`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgs'] });
      qc.invalidateQueries({ queryKey: orgKey(id) });
    },
  });
}

export function useMarkOrgSynthetic(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<OrgDto>(`/api/orgs/${orgId}/mark-synthetic`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgKey(orgId) });
      qc.invalidateQueries({ queryKey: ['orgs'] });
    },
  });
}

export interface DermConfigDto {
  organisation_id: string;
  base_url: string;
  username: string;
  has_password: boolean;
  created_at: string;
  updated_at: string;
}

export interface UpsertDermConfigInput {
  base_url: string;
  username: string;
  password?: string;
}

const dermConfigKey = (orgId: string) => ['org', orgId, 'derm-config'] as const;

export function useDermConfig(orgId: string | undefined) {
  return useQuery<DermConfigDto | null>({
    queryKey: dermConfigKey(orgId ?? ''),
    queryFn: () => apiGet<DermConfigDto | null>(`/api/orgs/${orgId}/derm-config`),
    enabled: !!orgId,
  });
}

export function useUpsertDermConfig(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertDermConfigInput) =>
      apiPatch<DermConfigDto>(`/api/orgs/${orgId}/derm-config`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dermConfigKey(orgId) });
    },
  });
}

export function useDeleteDermConfig(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete(`/api/orgs/${orgId}/derm-config`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dermConfigKey(orgId) });
    },
  });
}

/**
 * Lift the DERM credentials from another org into this one. The
 * source's plaintext password never leaves the server — clinical-api
 * decrypts with the source's DEK and re-encrypts under the target's
 * DEK in a single transaction.
 */
export function useCopyDermConfig(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fromOrgId: string) =>
      apiPost<DermConfigDto>(`/api/orgs/${orgId}/derm-config/copy-from`, {
        from_org_id: fromOrgId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dermConfigKey(orgId) });
    },
  });
}

export type DermTestConnectionResult =
  | { ok: true }
  | {
      ok: false;
      code: 'no_config' | 'unreachable' | 'invalid_credentials' | 'missing_token';
      message: string;
    };

export function useTestDermConnection(orgId: string) {
  return useMutation({
    mutationFn: () =>
      apiPost<DermTestConnectionResult>(`/api/orgs/${orgId}/derm-config/test-connection`),
  });
}

export interface OrgRetentionPolicy {
  retention_years: number | null;
}

const orgRetentionKey = (id: string) => ['org', id, 'retention-policy'] as const;

export function useOrgRetention(id: string | undefined) {
  return useQuery({
    queryKey: orgRetentionKey(id ?? ''),
    queryFn: () => apiGet<OrgRetentionPolicy>(`/api/orgs/${id}/retention-policy`),
    enabled: !!id,
  });
}

export function useSetOrgRetention(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (retention_years: number) =>
      apiPut<{ retention_years: number }>(`/api/orgs/${id}/retention-policy`, { retention_years }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgRetentionKey(id) });
    },
  });
}

export function usePreviewIsolation(orgId: string) {
  return useMutation({
    mutationFn: (input: IsolationSettingsInput) =>
      apiPost<IsolationPreview>(`/api/orgs/${orgId}/isolation/preview`, input),
  });
}

export function useApplyIsolation(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ApplyIsolationInput) =>
      apiPut<IsolationBlock>(`/api/orgs/${orgId}/isolation`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgs'] });
      qc.invalidateQueries({ queryKey: orgKey(orgId) });
    },
  });
}
